import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { userApi } from '@/api/userApi';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type ServerThemePreference = 'LIGHT' | 'DARK' | 'SYSTEM';

const STORAGE_KEY = 'rfo-theme-mode';
/** New users / first visits default to dark; they can switch to light from the UI. */
const DEFAULT_MODE: ThemeMode = 'dark';

interface ThemeContextValue {
  /** The user's chosen mode (may be 'system'). */
  mode: ThemeMode;
  /** The actual theme in effect after resolving 'system'. */
  resolvedTheme: ResolvedTheme;
  /** Set the mode. Persists to the DB unless persist=false (used when applying a value loaded from the server). */
  setMode: (mode: ThemeMode, persist?: boolean) => void;
  /** Convenience: flip between light and dark (sets an explicit mode). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getSystemTheme = (): ResolvedTheme =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const readStoredMode = (): ThemeMode => {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : DEFAULT_MODE;
};

const toServerPreference = (mode: ThemeMode): ServerThemePreference =>
  mode.toUpperCase() as ServerThemePreference;

export const fromServerPreference = (pref?: ServerThemePreference | null): ThemeMode | null => {
  if (pref === 'LIGHT' || pref === 'DARK' || pref === 'SYSTEM') {
    return pref.toLowerCase() as ThemeMode;
  }
  return null;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // Track OS preference changes while in 'system' mode.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: ResolvedTheme = mode === 'system' ? systemTheme : mode;

  // Reflect the resolved theme onto <html> so CSS variables swap.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setMode = useCallback((next: ThemeMode, persist = true) => {
    setModeState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
    // Persist to the DB so the preference follows the user across devices.
    // Fire-and-forget: a failure (e.g. not yet authenticated) leaves localStorage
    // as the fallback. `persist=false` is used when applying a value just loaded
    // from the server, to avoid echoing it straight back.
    if (persist) {
      userApi.updateMyTheme(toServerPreference(next)).catch(() => {
        /* ignore — localStorage keeps the choice locally */
      });
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setMode]);

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
